'use client';

import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext, closestCenter, DragOverlay, PointerSensor,
  useSensor, useSensors, useDroppable
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  TrendingUp, Plus, AlertCircle, X, Edit2, ThumbsUp,
  ThumbsDown, Calendar, User, Settings,
  CheckCircle, Crosshair, Zap, Clock, ArrowUpRight, BarChart3,
  Layers, ChevronRight, Activity, ShieldCheck, Trash2, MoreVertical,
  Download, Upload, ChevronUp, ChevronDown, ArrowLeftToLine, ArrowRightToLine
} from 'lucide-react';
import {
  fetchDeals, createDeal, updateDeal, moveDealStage,
  markDealWon, markDealLost, deleteDeal, fetchPipelines, clearError,
  createPipeline, updatePipeline, deletePipeline, fetchStaleDeals,
} from '../../redux/crmSlice';
import PageHeader from '../../components/PageHeader';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import ImportModal from '../../components/crm/ImportModal';
import DealOutcomeModal from '../../components/crm/DealOutcomeModal';
import { formatCurrency, getCurrencySymbol, convertToBase, convertCurrency } from '../../utils/currency';
import { useCompanyCurrency, useCurrencyRates } from '../../hooks/useCompanySettings';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkActionBar from '../../components/crm/BulkActionBar';
import RowActionsMenu from '../../components/crm/RowActionsMenu';
import { downloadCsv } from '../../utils/exportCsv';
import SimpleGanttChart from '../../components/charts/SimpleGanttChart';
import { PAGE, PageLoader } from '../../components/crm/ui';

const DEAL_STATUS_OPTIONS = [
  { value: 'open', label: 'OPEN' },
  { value: 'won', label: 'WON' },
  { value: 'lost', label: 'LOST' },
  { value: 'on_hold', label: 'ON HOLD' },
];

function fmtCurrency(val, currency) {
  return formatCurrency(val, currency, { compact: true });
}

const EMPTY_FORM = {
  title: '', value: '', currency: 'INR', stage: '', pipelineId: '',
  assignedTo: '', expectedCloseDate: '', probability: 50, notes: '',
};

const DEFAULT_STAGES = ['new', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];
const DEFAULT_STAGE_LABELS = {
  new: 'New', qualified: 'Qualified', proposal: 'Proposal', negotiation: 'Negotiation',
  closed_won: 'Closed Won', closed_lost: 'Closed Lost',
};

const STAGE_COLORS = ['#94a3b8','#60a5fa','#a78bfa','#f59e0b','#f97316','#22c55e','#ef4444','#0176D3','#ec4899'];

const BLANK_PIPELINE = {
  name: '',
  description: '',
  isDefault: false,
  stages: [
    { stageId: 'new', name: 'New', color: '#94a3b8', probability: 10, order: 1 },
    { stageId: 'qualified', name: 'Qualified', color: '#a78bfa', probability: 50, order: 2 },
    { stageId: 'proposal', name: 'Proposal Sent', color: '#f59e0b', probability: 70, order: 3 },
    { stageId: 'negotiation', name: 'Negotiation', color: '#f97316', probability: 85, order: 4 },
    { stageId: 'closed_won', name: 'Closed Won', color: '#22c55e', probability: 100, order: 5 },
    { stageId: 'closed_lost', name: 'Closed Lost', color: '#ef4444', probability: 0, order: 6 },
  ],
};

// ── Components ─────────────────────────────────────────────

function SortableDealCard({ deal, onEdit, onWon, onLost, onDelete, onClose, selected, onToggleSelect, companyCurrency }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: deal._id });
  const ratesCtx = useCurrencyRates();
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : 1 };
  const prob = deal.probability || 0;

  const probColor = prob >= 75 ? 'bg-[#2E844A]' : prob >= 50 ? 'bg-[#FF9900]' : 'bg-[#BA0517]';

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className={cn(
        "bg-white border border-[#E5E5E5] rounded-lg p-4 mb-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)] hover:border-[#0176D3] transition-all group cursor-grab active:cursor-grabbing",
        selected && "ring-2 ring-[#0176D3]",
        isDragging && "opacity-40 scale-95 ring-2 ring-[#0176D3] shadow-[0_2px_8px_rgba(0,0,0,0.16)]"
      )}>
        <div className="flex items-start justify-between mb-3">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(deal._id)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={`Select ${deal.title}`}
            className="w-4 h-4 mt-0.5 mr-2 accent-[#0176D3] shrink-0 cursor-pointer"
          />
          <div className="space-y-0.5 flex-1 pr-2 min-w-0">
            <h4 className="text-[13px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors truncate uppercase tracking-tight">{deal.title}</h4>
            {/* Normalized to the company's base currency — a board mixing each deal's
                own raw currency ($500 next to ₹1K side by side) reads as broken, not
                just inconsistent. */}
            <p className="text-lg font-bold text-[#080707] tabular-nums tracking-tight leading-none">{fmtCurrency(convertToBase(deal.value, deal.currency, ratesCtx), companyCurrency)}</p>
          </div>
        </div>

        <div className="space-y-1.5 mb-4">
          <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-wider">
            <span className="text-[#706E6B]">Confidence</span>
            <span className="text-[#080707]">{prob}%</span>
          </div>
          <div className="h-1.5 w-full bg-[#F3F2F2] rounded-full overflow-hidden border border-[#D8DDE6]">
            <div className={cn("h-full transition-all duration-1000", probColor)} style={{ width: `${prob}%` }} />
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-[#D8DDE6] opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1">
            <button onClick={() => onEdit(deal)} title="Edit" aria-label="Edit deal" className="p-1.5 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors"><Edit2 size={12} /></button>
            <button onClick={() => onDelete(deal)} title="Delete" aria-label="Delete deal" className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded transition-colors"><Trash2 size={12} /></button>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => onWon(deal)} className="p-1.5 text-[#2E844A] hover:bg-[#EBF3ED] rounded transition-colors" title="Mark Won"><ThumbsUp size={12} /></button>
            <button onClick={() => onLost(deal)} className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded transition-colors" title="Mark Lost"><ThumbsDown size={12} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DroppableColumn({ stage, label, deals, onEdit, onWon, onLost, onDelete, onClose, selectedIds, onToggleSelect }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const ratesCtx = useCurrencyRates();
  // Column total spans multiple deals — convert each into the base currency before
  // summing, and label with the base currency (Settings), not just one deal's currency.
  const totalVal = deals.reduce((sum, d) => sum + convertToBase(d.value || 0, d.currency, ratesCtx), 0);
  const columnCurrency = useCompanyCurrency();

  return (
    <div ref={setNodeRef} className={cn(
      "flex flex-col w-80 shrink-0 h-full rounded bg-[#F4F6F9] border border-[#D8DDE6] transition-colors",
      isOver && "border-[#0176D3] bg-[#0176D3]/[0.02]"
    )}>
      <div className="p-4 border-b border-[#D8DDE6] bg-white rounded-t">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-[#080707] uppercase tracking-wider">{label || stage}</span>
            <span className="text-[10px] font-bold text-[#706E6B] bg-[#F3F2F2] px-2 py-0.5 rounded-full">{deals.length}</span>
          </div>
          <div className="text-[14px] font-bold text-[#080707] tabular-nums">{fmtCurrency(totalVal, columnCurrency)}</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1 min-h-0">
        <SortableContext items={deals.map(d => d._id)} strategy={verticalListSortingStrategy}>
          {deals.map(deal => (
            <SortableDealCard
              key={deal._id}
              deal={deal}
              onEdit={onEdit}
              onWon={onWon}
              onLost={onLost}
              onDelete={onDelete}
              onClose={onClose}
              selected={selectedIds?.has(deal._id)}
              onToggleSelect={onToggleSelect}
              companyCurrency={columnCurrency}
            />
          ))}
        </SortableContext>
        {deals.length === 0 && (
          <div className="py-24 flex flex-col items-center justify-center border-2 border-dashed border-[#D8DDE6] rounded text-[10px] font-bold text-[#706E6B] uppercase tracking-widest opacity-40">
            Pipeline Clear
          </div>
        )}
      </div>
    </div>
  );
}

export default function DealsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { deals, pipelines, loading, error, staleDeals, staleDealsThreshold } = useSelector(state => state.crm);
  const companyCurrency = useCompanyCurrency();
  const ratesCtx = useCurrencyRates();
  const bulk = useBulkSelection();

  const handleCurrencyChange = (newCurrency) => {
    setFormData(p => {
      if (!p.value || p.currency === newCurrency) return { ...p, currency: newCurrency };

      // convertCurrency silently returns the raw value unchanged if either
      // currency has no configured exchange rate yet — without this check
      // the number just sits there relabeled as the new currency with no
      // indication anything went wrong, which reads as "conversion is broken".
      const hasRate = (c) => c === ratesCtx.baseCurrency || typeof ratesCtx.rates?.[c] === 'number';
      if (!hasRate(p.currency) || !hasRate(newCurrency)) {
        alertService.error(
          'Missing Exchange Rate',
          `No exchange rate configured for ${!hasRate(p.currency) ? p.currency : newCurrency}. Go to Settings → Currency to add rates — the value will auto-convert once rates are saved.`
        );
        // Revert — do NOT change the currency label without converting the amount.
        // Returning the old state keeps the selector on the original currency so
        // "$100" never becomes "₹100" with the same digits.
        return { ...p };
      }

      // Re-price the number itself, not just relabel it — $100 switched to
      // INR should become the INR equivalent, not "100" tagged as rupees.
      const converted = convertCurrency(p.value, p.currency, newCurrency, ratesCtx);
      return { ...p, currency: newCurrency, value: Math.round(converted * 100) / 100 };
    });
  };

  const [modal, setModal] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [activePipe, setActivePipe] = useState(null);
  const [view, setView] = useState('kanban');
  const [activeId, setActiveId] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showMenu, setShowMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [staleFilterActive, setStaleFilterActive] = useState(false);
  const [outcomeModal, setOutcomeModal] = useState({ open: false, mode: null, deal: null });

  // Pipeline Setup modal state
  const [pipeModal, setPipeModal] = useState(false);
  const [pipeView, setPipeView] = useState('list'); // 'list' | 'edit' | 'create'
  const [editingPipe, setEditingPipe] = useState(null);
  const [pipeForm, setPipeForm] = useState(BLANK_PIPELINE);
  const [pipeSaving, setPipeSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    dispatch(fetchPipelines());
  }, [dispatch]);

  useEffect(() => {
    if (pipelines.length > 0 && !activePipe) setActivePipe(pipelines[0]._id || pipelines[0].id);
  }, [pipelines, activePipe]);

  // Deals are scoped to whichever pipeline tab is active — without this, every
  // pipeline's kanban board renders ALL deals bucketed purely by matching stage
  // id/name, so two pipelines that share stage ids (e.g. the default "new",
  // "qualified", ... set) look like they contain identical, duplicated deals.
  useEffect(() => {
    if (activePipe) dispatch(fetchDeals({ pipelineId: activePipe, limit: 500 }));
  }, [dispatch, activePipe]);

  // Deal aging alerts — fetched once up front so the "Stale Deals" badge count
  // is available immediately, independent of which pipeline tab is active.
  useEffect(() => {
    dispatch(fetchStaleDeals(14));
  }, [dispatch]);

  const staleDealIds = useMemo(() => new Set(staleDeals.map(d => d._id)), [staleDeals]);

  const stages = useMemo(() => {
    if (activePipe && pipelines.length > 0) {
      const pipe = pipelines.find(p => (p._id || p.id) === activePipe);
      if (pipe?.stages?.length) return pipe.stages.map(s => (typeof s === 'string' ? s : (s.stageId || s.name)));
    }
    return DEFAULT_STAGES;
  }, [activePipe, pipelines]);

  const stageLabels = useMemo(() => {
    if (activePipe && pipelines.length > 0) {
      const pipe = pipelines.find(p => (p._id || p.id) === activePipe);
      if (pipe?.stages?.length) {
        const map = {};
        pipe.stages.forEach(s => {
          if (typeof s !== 'string') map[s.stageId || s.name] = s.name;
        });
        return map;
      }
    }
    return DEFAULT_STAGE_LABELS;
  }, [activePipe, pipelines]);

  const grouped = useMemo(() => {
    const g = {};
    stages.forEach(s => { g[s] = []; });
    const visibleDeals = staleFilterActive ? deals.filter(d => staleDealIds.has(d._id)) : deals;
    visibleDeals.forEach(d => {
      const k = d.stage || stages[0];
      if (g[k]) g[k].push(d);
      else if (stages.length > 0) g[stages[0]].push(d);
    });
    return g;
  }, [deals, stages, staleFilterActive, staleDealIds]);

  const stageColors = useMemo(() => {
    const map = {};
    if (activePipe && pipelines.length > 0) {
      const pipe = pipelines.find(p => (p._id || p.id) === activePipe);
      if (pipe?.stages?.length) {
        pipe.stages.forEach((s, i) => {
          if (typeof s !== 'string') map[s.stageId || s.name] = s.color || STAGE_COLORS[i % STAGE_COLORS.length];
        });
        return map;
      }
    }
    stages.forEach((s, i) => { map[s] = STAGE_COLORS[i % STAGE_COLORS.length]; });
    return map;
  }, [activePipe, pipelines, stages]);

  // A deal created directly into a stage (never dragged) has an empty
  // stageHistory — falls back to one solid segment in its current stage's
  // color rather than showing a blank bar.
  const ganttRows = useMemo(() => {
    const visibleDeals = staleFilterActive ? deals.filter(d => staleDealIds.has(d._id)) : deals;
    return visibleDeals.map(deal => {
      const created = new Date(deal.createdAt);
      const closed = deal.actualCloseDate ? new Date(deal.actualCloseDate) : null;
      const target = deal.expectedCloseDate ? new Date(deal.expectedCloseDate) : null;
      const end = closed || target || new Date();
      const noTargetDate = !closed && !target;

      const history = deal.stageHistory || [];
      let segments;
      if (history.length > 0) {
        // stageHistory only logs each MOVE (the stage moved into, and when) —
        // the span before the very first move has no recorded stage, so it's
        // absorbed into that first entry's color rather than guessed at.
        const moveTimes = history.map(h => new Date(h.movedAt));
        segments = history.map((h, i) => ({
          start: i === 0 ? created : moveTimes[i],
          end: moveTimes[i + 1] || end,
          color: stageColors[h.stage] || '#94a3b8',
          tooltip: `${stageLabels[h.stage] || h.stage} — ${moveTimes[i].toLocaleDateString('en-IN')}`,
          dashed: i === history.length - 1 && noTargetDate,
        }));
      } else {
        segments = [{
          start: created, end,
          color: stageColors[deal.stage] || '#94a3b8',
          tooltip: `${stageLabels[deal.stage] || deal.stage}`,
          dashed: noTargetDate,
        }];
      }
      return { id: deal._id, label: deal.title, segments };
    });
  }, [deals, staleFilterActive, staleDealIds, stageColors, stageLabels]);

  const openAdd = () => { setEditDeal(null); setFormData({ ...EMPTY_FORM, pipelineId: activePipe || '' }); setModal(true); };
  const openEdit = (deal) => { setEditDeal(deal); setFormData(deal); setModal(true); };
  const closeModal = () => { setModal(false); setEditDeal(null); dispatch(clearError()); };

  const handleExportDeals = async () => {
    setShowMenu(false);
    try {
      const response = await api.get('/crm/deals/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deals-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alertService.error('Export Failed', err.response?.data?.message || 'Could not export deals');
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaveLoading(true);
    try {
      if (editDeal?._id) {
        await dispatch(updateDeal({ id: editDeal._id, data: formData })).unwrap();
        alertService.success('Updated', 'Deal record updated');
      } else {
        await dispatch(createDeal(formData)).unwrap();
        alertService.success('Success', 'New deal added to pipeline');
      }
      closeModal();
    } catch (err) {
      alertService.error('Error', err || 'Failed to save deal');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleWon = (deal) => setOutcomeModal({ open: true, mode: 'won', deal });
  const handleLost = (deal) => setOutcomeModal({ open: true, mode: 'lost', deal });
  const closeOutcomeModal = () => setOutcomeModal({ open: false, mode: null, deal: null });

  const handleOutcomeConfirm = async ({ reasonCategory, note, competitorName }) => {
    const { mode, deal } = outcomeModal;
    if (!deal) return;
    try {
      if (mode === 'won') {
        const result = await dispatch(markDealWon({ id: deal._id, winReason: reasonCategory, note })).unwrap();
        closeOutcomeModal();
        const invoiceNow = await alertService.confirm(
          'Deal won — create the invoice now?',
          result?.promotedToCustomer ? 'The contact/account was promoted to Customer.' : 'You can also do this later from the deal.',
          'success', 'Create invoice'
        );
        if (invoiceNow) navigate(`/dashboard/crm/deals/${deal._id}?createInvoice=1`);
        return;
      } else {
        await dispatch(markDealLost({ id: deal._id, lostReason: reasonCategory, note, competitorName })).unwrap();
        alertService.success('Success', 'Deal marked as lost');
      }
      closeOutcomeModal();
    } catch (err) {
      alertService.error('Error', err || 'Action failed');
    }
  };

  const handleDelete = async (deal) => {
    const confirmed = await alertService.confirm('Delete Deal?', `Permanently delete "${deal.title}"?`, 'danger', 'Delete');
    if (confirmed) {
      try {
        await dispatch(deleteDeal(deal._id)).unwrap();
        alertService.success('Success', 'Deal deleted');
      } catch (err) { alertService.error('Error', err || 'Action failed'); }
    }
  };

  const runBulkAction = async (action, value) => {
    try {
      const res = await api.post('/crm/deals/bulk', { dealIds: bulk.selectedIds, action, value });
      const count = res.data?.modifiedCount ?? bulk.selectedCount;
      bulk.clear();
      dispatch(fetchDeals({ pipelineId: activePipe, limit: 500 }));
      alertService.success(action === 'delete' ? 'Deleted' : 'Updated', `${count} deal(s) ${action === 'delete' ? 'deleted' : 'updated'}`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Bulk action failed');
    }
  };

  const handleBulkStatusChange = (value) => runBulkAction('change_status', value);
  const handleBulkAssign = (value) => runBulkAction('assign', value);
  const handleBulkDelete = () => runBulkAction('delete');

  const handleBulkExport = () => {
    const selected = deals.filter(d => bulk.selected.has(d._id));
    downloadCsv(
      'deals-selected.csv',
      ['Title', 'Value', 'Currency', 'Status', 'Stage', 'AssignedTo'],
      selected.map(d => [d.title, d.value, d.currency, d.status, d.stage, d.assignedTo || ''])
    );
  };

  // ── Pipeline Setup handlers ───────────────────────────────
  const openPipeModal = () => { setPipeView('list'); setEditingPipe(null); setPipeModal(true); };
  const closePipeModal = () => { setPipeModal(false); setEditingPipe(null); setPipeView('list'); };

  const startCreatePipe = () => {
    setPipeForm({ ...BLANK_PIPELINE, stages: BLANK_PIPELINE.stages.map(s => ({ ...s })) });
    setEditingPipe(null);
    setPipeView('create');
  };

  const startEditPipe = (pipe) => {
    setEditingPipe(pipe);
    setPipeForm({
      name: pipe.name,
      description: pipe.description || '',
      isDefault: pipe.isDefault || false,
      stages: (pipe.stages || []).map((s, i) => ({ ...s, order: s.order || i + 1 })),
    });
    setPipeView('edit');
  };

  const handlePipeSave = async () => {
    if (!pipeForm.name.trim()) return alertService.error('Error', 'Pipeline name is required');
    if (pipeForm.stages.length === 0) return alertService.error('Error', 'Add at least one stage');
    setPipeSaving(true);
    try {
      const payload = {
        ...pipeForm,
        stages: pipeForm.stages.map((s, i) => ({ ...s, order: i + 1, stageId: s.stageId || s.name.toLowerCase().replace(/\s+/g, '_') }),
        ),
      };
      if (pipeView === 'create') {
        const result = await dispatch(createPipeline(payload)).unwrap();
        const newId = (result.pipeline || result)._id;
        setActivePipe(newId);
        alertService.success('Created', 'Pipeline created successfully');
      } else {
        await dispatch(updatePipeline({ id: editingPipe._id, data: payload })).unwrap();
        alertService.success('Saved', 'Pipeline updated');
      }
      setPipeView('list');
      setEditingPipe(null);
    } catch (err) {
      alertService.error('Error', err || 'Failed to save pipeline');
    } finally {
      setPipeSaving(false);
    }
  };

  const handlePipeDelete = async (pipe) => {
    const confirmed = await alertService.confirm('Delete Pipeline?', `Delete "${pipe.name}" and all its stage config?`, 'danger', 'Delete');
    if (!confirmed) return;
    try {
      await dispatch(deletePipeline(pipe._id)).unwrap();
      if (activePipe === pipe._id) setActivePipe(null);
      alertService.success('Deleted', 'Pipeline removed');
    } catch (err) {
      alertService.error('Error', err || 'Failed to delete');
    }
  };

  const addStage = () => {
    const colorIdx = pipeForm.stages.length % STAGE_COLORS.length;
    setPipeForm(f => ({
      ...f,
      stages: [...f.stages, { stageId: '', name: '', color: STAGE_COLORS[colorIdx], probability: 50, order: f.stages.length + 1 }],
    }));
  };

  // Inserts a blank stage immediately before/after a given row — `order` is
  // just recomputed from array position on save, so the new stage only needs
  // to land at the right index here.
  const insertStage = (idx, position) => {
    const colorIdx = pipeForm.stages.length % STAGE_COLORS.length;
    const blank = { stageId: '', name: '', color: STAGE_COLORS[colorIdx], probability: 50, order: 0 };
    setPipeForm(f => {
      const stages = [...f.stages];
      stages.splice(position === 'before' ? idx : idx + 1, 0, blank);
      return { ...f, stages };
    });
  };

  const moveStage = (idx, direction) => {
    setPipeForm(f => {
      const stages = [...f.stages];
      const target = idx + direction;
      if (target < 0 || target >= stages.length) return f;
      [stages[idx], stages[target]] = [stages[target], stages[idx]];
      return { ...f, stages };
    });
  };

  const removeStage = (idx) => setPipeForm(f => ({ ...f, stages: f.stages.filter((_, i) => i !== idx) }));

  const updateStage = (idx, field, val) => setPipeForm(f => ({
    ...f,
    stages: f.stages.map((s, i) => i === idx ? { ...s, [field]: val } : s),
  }));

  const handleDragEnd = useCallback(({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    // Dropping near/on an existing card resolves `over.id` to that card's deal id,
    // not the column — since only the column itself is a useDroppable target. Fall
    // back to that card's own stage so a drop anywhere inside a populated column
    // (not just its empty whitespace) still moves the dragged deal there.
    let newStage = over.id;
    if (!stages.includes(newStage)) {
      const overDeal = deals.find(d => d._id === over.id);
      newStage = overDeal?.stage;
    }
    if (newStage && stages.includes(newStage)) {
      const deal = deals.find(d => d._id === active.id);
      if (deal && deal.stage !== newStage) {
        // Closed Won/Lost must always capture a structured reason — dropping a
        // card straight into one of these columns used to call moveStage
        // directly, silently closing the deal with no reason recorded at all.
        // Route it through the same outcome modal the toolbar buttons use instead.
        if (newStage === 'closed_won') {
          setOutcomeModal({ open: true, mode: 'won', deal });
        } else if (newStage === 'closed_lost') {
          setOutcomeModal({ open: true, mode: 'lost', deal });
        } else {
          dispatch(moveDealStage({ id: active.id, stage: newStage })).unwrap().catch((err) => {
            alertService.error('Error', err || 'Failed to move deal — reverted to its previous stage');
          });
        }
      }
    }
  }, [deals, stages, dispatch]);

  const activeDeal = activeId ? deals.find(d => d._id === activeId) : null;
  const openDealsList = deals.filter(d => d.status === 'open' || !d.status);
  const wonDealsCount = deals.filter(d => d.status === 'won').length;
  const lostDealsCount = deals.filter(d => d.status === 'lost').length;
  // Won / (won + lost) alone ignores every deal still open — a single early win
  // with nothing marked lost yet pins this at 100% even though most of the
  // pipeline hasn't been decided. Dividing by the whole pipeline instead means
  // the rate only climbs as deals actually close, not the moment the first one does.
  const winRate = deals.length > 0
    ? Math.round((wonDealsCount / deals.length) * 1000) / 10
    : 0;
  // Open pipeline value/probability only — a closed deal shouldn't count toward what's still in flight.
  // Each deal is converted into the base currency before summing so mixed-currency
  // deals don't just get added together as raw numbers under one currency label.
  const totalPipeValue = openDealsList.reduce((sum, d) => sum + convertToBase(d.value || 0, d.currency, ratesCtx), 0);
  // Aggregate total spans multiple deals — label with the company's base currency,
  // not just one deal's currency, so it responds to a Settings change.
  const pipelineCurrency = companyCurrency;

  if (loading && deals.length === 0) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className={PAGE}>
      
      {/* Page Header */}
      <PageHeader
        title="Deals Pipeline"
        subtitle={<>{openDealsList.length} Active Opportunities • {fmtCurrency(totalPipeValue, pipelineCurrency)} Total</>}
        icon={TrendingUp}
        actions={<>
<button
            onClick={() => setStaleFilterActive(v => !v)}
            title={`Deals stuck in their current stage for ${staleDealsThreshold}+ days`}
            className={cn(
              "h-9 px-4 rounded text-[13px] font-bold transition-all flex items-center gap-2 border whitespace-nowrap shrink-0",
              staleFilterActive
                ? "bg-[#BA0517] text-white border-[#BA0517] shadow-[0_2px_8px_rgba(0,0,0,0.16)]"
                : "bg-white text-[#BA0517] border-[#D8DDE6] hover:bg-[#FDE7E9]"
            )}
          >
            <Clock size={14} /> Stale Deals ({staleDealsThreshold}d+)
            <span className={cn(
              "ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold",
              staleFilterActive ? "bg-white/20 text-white" : "bg-[#FDE7E9] text-[#BA0517]"
            )}>
              {staleDeals.length}
            </span>
          </button>
          <button onClick={openPipeModal} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Settings size={14} /> Pipeline Setup
          </button>
          <button onClick={openAdd} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Plus size={14} /> New Deal
          </button>
          <div className="relative">
            <button
              onClick={() => setShowMenu(v => !v)}
              title="More options"
              aria-label="More options"
              className="h-9 w-9 flex items-center justify-center bg-white border border-[#D8DDE6] rounded text-[#706E6B] hover:bg-[#F3F2F2] transition-all"
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute top-full right-0 mt-2 z-20 bg-white border border-[#D8DDE6] rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] py-1 w-48">
                  <button onClick={handleExportDeals} className="w-full text-left px-4 py-2 text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] transition-colors flex items-center gap-2">
                    <Upload size={14} /> Export CSV
                  </button>
                  <button onClick={() => { setShowMenu(false); setShowImportModal(true); }} className="w-full text-left px-4 py-2 text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] transition-colors flex items-center gap-2">
                    <Download size={14} /> Import
                  </button>
                </div>
              </>
            )}
          </div>
  </>}
      />

      <ImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Deals"
        importEndpoint="/crm/deals/import"
        sampleHeaders={['Title', 'Value', 'Status', 'Stage', 'Notes']}
        sampleRow={['Renewal - Acme Corp', '5000', 'open', 'new', 'Follow up next week']}
        onImported={() => dispatch(fetchDeals({ pipelineId: activePipe, limit: 500 }))}
      />

      <DealOutcomeModal
        open={outcomeModal.open}
        mode={outcomeModal.mode}
        dealTitle={outcomeModal.deal?.title}
        onClose={closeOutcomeModal}
        onConfirm={handleOutcomeConfirm}
      />

      {/* KPI Matrix */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="sf-card p-6 border-l-4 border-[#0176D3]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Total Pipeline</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{fmtCurrency(totalPipeValue, pipelineCurrency)}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#DD7A01]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Avg Probability</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">
            {openDealsList.length ? Math.round(openDealsList.reduce((s,d)=>s+(d.probability||0),0)/openDealsList.length) + '%' : '0%'}
          </h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#FF9900]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Win Rate</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums mb-2">{winRate}%</h3>
          <div className="h-1.5 w-full bg-[#F3F2F2] rounded-full overflow-hidden border border-[#D8DDE6]">
            <div
              className="h-full bg-[#FF9900] transition-all duration-1000"
              style={{ width: `${Math.min(100, winRate)}%` }}
            />
          </div>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#2E844A]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Active Deals</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{openDealsList.length}</h3>
        </div>
      </div>

      {/* Pipeline Selector + View Toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {pipelines.length > 1 ? (
          <div className="flex items-center gap-1 p-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded-md w-fit">
            {pipelines.map(p => {
              const pid = p._id || p.id;
              const active = activePipe === pid;
              return (
                <button
                  key={pid}
                  className={cn(
                    "px-4 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-all",
                    active ? "bg-white text-[#0176D3] shadow-sm border border-[#D8DDE6]" : "text-[#706E6B] hover:text-[#0176D3]"
                  )}
                  onClick={() => setActivePipe(pid)}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        ) : <div />}
        <div className="flex items-center gap-1 p-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded-md w-fit">
          {['kanban', 'gantt'].map(v => (
            <button
              key={v}
              className={cn(
                "px-4 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-all",
                view === v ? "bg-white text-[#0176D3] shadow-sm border border-[#D8DDE6]" : "text-[#706E6B] hover:text-[#0176D3]"
              )}
              onClick={() => setView(v)}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk selection controls */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={bulk.isAllSelected(deals.map(d => d._id))}
            onChange={() => bulk.toggleAll(deals.map(d => d._id))}
            className="w-4 h-4 accent-[#0176D3] cursor-pointer"
          />
          <span className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider">Select all visible deals</span>
        </label>
      </div>
      <BulkActionBar
        selectedCount={bulk.selectedCount}
        onClear={bulk.clear}
        statusOptions={DEAL_STATUS_OPTIONS}
        onStatusChange={handleBulkStatusChange}
        onAssign={handleBulkAssign}
        assignPlaceholder="Assign to…"
        onDelete={handleBulkDelete}
        onExport={handleBulkExport}
        entityLabel="deal"
      />

      {/* Gantt View */}
      {view === 'gantt' && (
        <SimpleGanttChart rows={ganttRows} emptyMessage="No deals to plot yet." />
      )}

      {/* Kanban Board */}
      {view === 'kanban' && (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={({ active }) => setActiveId(active.id)} onDragEnd={handleDragEnd}>
        {/* Fixed board height so the horizontal scrollbar sits at a stable position at
            the bottom of the board instead of drifting away as columns grow taller —
            each column scrolls its own cards internally via its own overflow-y-auto. */}
        <div className="flex gap-6 overflow-x-auto pb-3 h-[65vh] py-2 select-none">
          {stages.map(stage => (
            <DroppableColumn
              key={stage}
              stage={stage}
              label={stageLabels[stage]}
              deals={grouped[stage] || []}
              onEdit={openEdit}
              onWon={handleWon}
              onLost={handleLost}
              onDelete={handleDelete}
              selectedIds={bulk.selected}
              onToggleSelect={bulk.toggle}
            />
          ))}
        </div>
        <DragOverlay>
          {activeDeal ? (
            <div className="w-80 p-4 rounded bg-white border-2 border-[#0176D3] shadow-[0_2px_8px_rgba(0,0,0,0.16)] rotate-1 scale-105 opacity-95 backdrop-blur-md">
              <p className="text-[10px] font-bold text-[#706E6B] uppercase mb-1">MOVING NODE</p>
              <h4 className="text-[13px] font-bold text-[#080707] uppercase">{activeDeal.title}</h4>
              <p className="text-lg font-bold text-[#080707] mt-1">{fmtCurrency(convertToBase(activeDeal.value, activeDeal.currency, ratesCtx), pipelineCurrency)}</p>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      )}

      {/* Pipeline Setup Modal */}
      {pipeModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closePipeModal} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-2xl z-10 max-h-[90vh] animate-in zoom-in-95 duration-200 flex flex-col">
            {/* Header */}
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white shrink-0">
              <div className="flex items-center gap-2">
                {pipeView !== 'list' && (
                  <button onClick={() => setPipeView('list')} className="text-[#706E6B] hover:text-[#0176D3] mr-1">
                    <ChevronRight size={16} className="rotate-180" />
                  </button>
                )}
                <Settings size={16} className="text-[#0176D3]" />
                <h3 className="text-[16px] font-bold text-[#2E2E2E]">
                  {pipeView === 'list' ? 'Pipeline Setup' : pipeView === 'create' ? 'Create Pipeline' : `Edit: ${editingPipe?.name}`}
                </h3>
              </div>
              <button onClick={closePipeModal} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
            </div>

            <div className="overflow-y-auto flex-1">
              {/* List view */}
              {pipeView === 'list' && (
                <div className="p-4 sm:p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[12px] text-[#706E6B] font-medium">Manage your sales pipelines and stages</p>
                    <button onClick={startCreatePipe} className="h-8 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] flex items-center gap-1.5 whitespace-nowrap shrink-0">
                      <Plus size={13} /> New Pipeline
                    </button>
                  </div>

                  {pipelines.length === 0 ? (
                    <div className="py-16 flex flex-col items-center justify-center border-2 border-dashed border-[#D8DDE6] rounded-lg gap-3">
                      <Layers size={32} className="text-[#D8DDE6]" />
                      <p className="text-[12px] text-[#706E6B] font-medium">No pipelines yet</p>
                      <button onClick={startCreatePipe} className="h-8 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] flex items-center gap-1.5 whitespace-nowrap shrink-0">
                        <Plus size={13} /> Create Your First Pipeline
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pipelines.map(pipe => (
                        <div key={pipe._id} className="flex items-center justify-between p-4 border border-[#D8DDE6] rounded-lg hover:border-[#0176D3] transition-colors group">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded bg-[#EBF5FF] flex items-center justify-center">
                              <Activity size={14} className="text-[#0176D3]" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-[13px] font-bold text-[#080707]">{pipe.name}</span>
                                {pipe.isDefault && <span className="text-[9px] font-bold bg-[#EBF5FF] text-[#0176D3] px-1.5 py-0.5 rounded-full uppercase tracking-wide">Default</span>}
                              </div>
                              <span className="text-[11px] text-[#706E6B]">{pipe.stages?.length || 0} stages</span>
                            </div>
                          </div>
                          <RowActionsMenu actions={[
                            { label: 'Edit', icon: Edit2, onClick: () => startEditPipe(pipe) },
                            { label: 'Delete', icon: Trash2, danger: true, onClick: () => handlePipeDelete(pipe) },
                          ]} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Create / Edit view */}
              {(pipeView === 'create' || pipeView === 'edit') && (
                <div className="p-4 sm:p-6 space-y-6">
                  {/* Pipeline name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-[#706E6B]">Pipeline Name *</label>
                      <input
                        value={pipeForm.name}
                        onChange={e => setPipeForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Sales Pipeline"
                        className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-[#706E6B]">Description</label>
                      <input
                        value={pipeForm.description}
                        onChange={e => setPipeForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Optional description"
                        className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]"
                      />
                    </div>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer w-fit">
                    <input type="checkbox" checked={pipeForm.isDefault} onChange={e => setPipeForm(f => ({ ...f, isDefault: e.target.checked }))} className="w-4 h-4 accent-[#0176D3]" />
                    <span className="text-[12px] font-bold text-[#080707]">Set as default pipeline</span>
                  </label>

                  {/* Stages */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-semibold text-[#706E6B]">Stages ({pipeForm.stages.length})</label>
                      <button onClick={addStage} className="h-7 px-3 bg-[#EBF5FF] text-[#0176D3] rounded text-[11px] font-bold hover:bg-[#0176D3] hover:text-white transition-colors flex items-center gap-1">
                        <Plus size={12} /> Add Stage
                      </button>
                    </div>

                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {pipeForm.stages.map((stage, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-3 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg">
                          <div className="flex items-center justify-center w-5 h-5 shrink-0">
                            <span className="text-[10px] font-bold text-[#706E6B]">{idx + 1}</span>
                          </div>
                          {/* Reorder — shuffles this stage's position; order is recomputed from
                              array index on save, so the pipeline everywhere else (Kanban columns,
                              deal progression, analytics) follows whatever order is set here. */}
                          <div className="flex flex-col shrink-0">
                            <button type="button" onClick={() => moveStage(idx, -1)} disabled={idx === 0} title="Move up" className="p-0.5 text-[#706E6B] hover:text-[#0176D3] disabled:opacity-25 disabled:cursor-not-allowed"><ChevronUp size={14} /></button>
                            <button type="button" onClick={() => moveStage(idx, 1)} disabled={idx === pipeForm.stages.length - 1} title="Move down" className="p-0.5 text-[#706E6B] hover:text-[#0176D3] disabled:opacity-25 disabled:cursor-not-allowed"><ChevronDown size={14} /></button>
                          </div>
                          <input
                            value={stage.name}
                            onChange={e => updateStage(idx, 'name', e.target.value)}
                            placeholder="Stage name"
                            className="flex-1 h-8 px-2 bg-white border border-[#D8DDE6] rounded text-[12px] focus:border-[#0176D3] outline-none min-w-0 focus:ring-1 focus:ring-[#0176D3]"
                          />
                          <input
                            type="color"
                            value={stage.color || '#94a3b8'}
                            onChange={e => updateStage(idx, 'color', e.target.value)}
                            className="w-8 h-8 rounded border border-[#D8DDE6] cursor-pointer p-0.5 bg-white shrink-0"
                            title="Stage color"
                          />
                          <div className="flex items-center gap-1 shrink-0">
                            <input
                              type="number" min="0" max="100"
                              value={stage.probability}
                              onChange={e => updateStage(idx, 'probability', Number(e.target.value))}
                              className="w-14 h-8 px-2 bg-white border border-[#D8DDE6] rounded text-[12px] text-center focus:border-[#0176D3] outline-none focus:ring-1 focus:ring-[#0176D3]"
                              title="Win probability %"
                            />
                            <span className="text-[10px] text-[#706E6B] font-bold">%</span>
                          </div>
                          <button type="button" onClick={() => insertStage(idx, 'before')} title="Insert stage before" className="text-[#0176D3] hover:bg-[#EBF5FF] rounded p-1 shrink-0 transition-colors"><ArrowLeftToLine size={13} /></button>
                          <button type="button" onClick={() => insertStage(idx, 'after')} title="Insert stage after" className="text-[#0176D3] hover:bg-[#EBF5FF] rounded p-1 shrink-0 transition-colors"><ArrowRightToLine size={13} /></button>
                          <button onClick={() => removeStage(idx)} className="text-[#BA0517] hover:bg-[#FDE7E9] rounded p-1 shrink-0 transition-colors">
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                    {pipeForm.stages.length === 0 && (
                      <div className="py-8 text-center text-[11px] text-[#706E6B] border-2 border-dashed border-[#D8DDE6] rounded-lg">
                        No stages yet — add at least one
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {(pipeView === 'create' || pipeView === 'edit') && (
              <div className="px-6 py-4 border-t border-[#D8DDE6] bg-[#F4F6F9] flex gap-3 shrink-0">
                <button onClick={() => setPipeView('list')} className="h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-white">Cancel</button>
                <button onClick={handlePipeSave} disabled={pipeSaving} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {pipeSaving ? 'Saving…' : pipeView === 'create' ? 'Create Pipeline' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deal Modal */}
      {modal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-xl z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">{editDeal?._id ? 'Refine Opportunity' : 'New Strategic Deal'}</h3>
              <button onClick={closeModal} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
            </div>
            
            <form onSubmit={handleSave} className="p-4 sm:p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Deal Title</label>
                <div className="relative">
                  <Crosshair className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                  <input required value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Q4 ENTERPRISE RENEWAL" className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Valuation</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B] text-[13px] font-bold">{getCurrencySymbol(formData.currency)}</span>
                    <input type="number" required min="0" step="0.01" value={formData.value} onChange={e => setFormData(p => ({ ...p, value: e.target.value }))} placeholder="0.00" className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Currency</label>
                  <select value={formData.currency} onChange={e => handleCurrencyChange(e.target.value)} className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]">
                    {['INR', 'USD', 'EUR', 'GBP'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Pipeline Stage</label>
                  <select value={formData.stage} onChange={e => setFormData(p => ({ ...p, stage: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]">
                    <option value="">SELECT STAGE</option>
                    {stages.map(s => <option key={s} value={s}>{(stageLabels[s] || s).toUpperCase()}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Confidence (%)</label>
                  <div className="relative">
                    <Zap className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                    <input type="number" min="0" max="100" value={formData.probability} onChange={e => setFormData(p => ({ ...p, probability: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Expected Close Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                  <input type="date" value={formData.expectedCloseDate ? new Date(formData.expectedCloseDate).toISOString().split('T')[0] : ''} onChange={e => setFormData(p => ({ ...p, expectedCloseDate: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
                <button type="button" onClick={closeModal} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {saveLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
